import { apiService } from '@/services/APIService';
import { rHandler } from '@/plugins/RHandler';
import { objectsRHandler, DEFAULT_PARAMS } from '@/utils/objectsRHandler';

import Subordination from '../list/subordination';
import Participant from '../list/participant';
import Team from '../list/team';
import { PER_PAGE } from '@/constants/pagination';

const API_PATH = 'event-subordinations';

export const SubordinationManager = {
  objects: {
    ...objectsRHandler(API_PATH, Subordination),

    getActivityByHigher({ args = [], params = {} } = DEFAULT_PARAMS) {
      const [event, step] = args;
      return this.showByHigher({
        params: {
          expand: 'event,organizer',
          ...params,
          'search[root_event_id]': event.highest_event?.id || event.parent_event_id || event.id,
          'search[event_level]': step.id,
          ...(step.region?.id ? { 'search[event_area_region_id]': step.region.id } : {}),
          ...(step.municipality?.id ? { 'search[event_area_municipality_id]': step.municipality.id } : {}),
        },
      });
    },

    getActivityStatisticsByHigher({ args = [] } = DEFAULT_PARAMS) {
      return this.getActivityByHigher({
        args,
        params: { 'per-page': PER_PAGE.infinity, fields: 'event', expand: 'event' },
      });
    },

    showByHigher({ params } = DEFAULT_PARAMS) {
      return rHandler(
        apiService.get(`${API_PATH}/by-higher`, params),
        (response) => ({
          response,
          items: response.data.data.map((i) => new Subordination(i, API_PATH)),
        }),
        (error) => ({ error }),
      );
    },

    // Users
    getUsers({ args = [], params } = DEFAULT_PARAMS) {
      const [id] = args;
      return rHandler(
        apiService.get(`${API_PATH}/${id}/users`, params),
        (response) => ({
          response,
          items: response.data.data.map((i) => new Participant(i, 'users')),
        }),
        (error) => ({ error }),
      );
    },

    getAllUsers({ args = [], params } = DEFAULT_PARAMS) {
      const [eventId, nominationId] = args;
      return rHandler(
        apiService.get(`${API_PATH}/events/${eventId}/nominations/${nominationId}/users`, params),
        (response) => ({
          response,
          items: response.data.data.map((i) => new Participant(i, 'users')),
        }),
        (error) => ({ error }),
      );
    },

    rejectUsers({ args = [], data } = DEFAULT_PARAMS) {
      const [id] = args;
      return rHandler(
        apiService.post(`/${API_PATH}/${id}/users-reject`, data),
        (response) => ({ response }),
        (error) => ({ error }),
      );
    },

    confirmUsers({ args = [], data } = DEFAULT_PARAMS) {
      const [id] = args;
      return rHandler(
        apiService.post(`/${API_PATH}/${id}/users-confirm`, data),
        (response) => ({ response }),
        (error) => ({ error }),
      );
    },

    expelUsers({ args = [], data } = DEFAULT_PARAMS) {
      const [id] = args;
      return rHandler(
        apiService.post(`/${API_PATH}/${id}/users-expel`, data),
        (response) => ({ response }),
        (error) => ({ error }),
      );
    },

    transferUsers({ args = [], data } = DEFAULT_PARAMS) {
      const [id] = args;
      return rHandler(
        apiService.post(`/${API_PATH}/${id}/users`, data),
        (response) => ({ response }),
        (error) => ({ error }),
      );
    },

    forceTransfer({ args = [], data } = DEFAULT_PARAMS) {
      const [id] = args;
      return rHandler(
        apiService.post(`/${API_PATH}/${id}/force-transfer`, data),
        (response) => ({ response }),
        (error) => ({ error }),
      );
    },

    pullUsers({ args = [], data } = DEFAULT_PARAMS) {
      const [id] = args;
      return rHandler(
        apiService.post(`/${API_PATH}/${id}/users/pull`, data),
        (response) => ({ response }),
        (error) => ({ error }),
      );
    },

    requestUsers({ args = [], data } = DEFAULT_PARAMS) {
      const [id] = args;
      return rHandler(
        apiService.post(`/${API_PATH}/${id}/users/pull-request`, data),
        (response) => ({ response }),
        (error) => ({ error }),
      );
    },

    cancelUsers({ args = [], data } = DEFAULT_PARAMS) {
      const [id] = args;
      return rHandler(
        apiService.delete(`/${API_PATH}/${id}/users`, { data }),
        (response) => ({ response }),
        (error) => ({ error }),
      );
    },

    // Teams
    getTeams({ args = [], params } = DEFAULT_PARAMS) {
      const [id] = args;
      return rHandler(
        apiService.get(`${API_PATH}/${id}/teams`, params),
        (response) => ({
          response,
          items: response.data.data.map((i) => new Team(i, 'teams')),
        }),
        (error) => ({ error }),
      );
    },

    getAllTeams({ args = [], params } = DEFAULT_PARAMS) {
      const [eventId, nominationId] = args;
      return rHandler(
        apiService.get(`${API_PATH}/events/${eventId}/nominations/${nominationId}/teams`, params),
        (response) => ({
          response,
          items: response.data.data.map((i) => new Team(i, 'teams')),
        }),
        (error) => ({ error }),
      );
    },

    rejectTeams({ args = [], data } = DEFAULT_PARAMS) {
      const [id] = args;
      return rHandler(
        apiService.post(`/${API_PATH}/${id}/teams-reject`, data),
        (response) => ({ response }),
        (error) => ({ error }),
      );
    },

    confirmTeams({ args = [], data } = DEFAULT_PARAMS) {
      const [id] = args;
      return rHandler(
        apiService.post(`/${API_PATH}/${id}/teams-confirm`, data),
        (response) => ({ response }),
        (error) => ({ error }),
      );
    },
  },

  async withOrganizations({ params } = DEFAULT_PARAMS) {
    const { response, items } = await this.objects.index({ params });

    if (items.length > 0) {
      await rHandler(
        apiService.get('/organizers', { 'search[id]': items.map((i) => i.organizer_id) }),
        (resp) => {
          resp.data.data.forEach((i, idx) => {
            items[idx].setOrganization(i);
          });
          return resp;
        },
        (error) => ({ error }),
      );
    }

    return { response, items };
  },
};
